import { useEffect } from "react";

/*
  Resilient muted autoplay for the hero films (Safari on Mac, 2026-10-17).

  What went wrong: the film called `video.play()` exactly ONCE, the moment the
  element mounted. Safari refuses that first call more often than Chrome does -
  the tab was opened in the background (or the window was not frontmost while
  the page loaded), Safari's per-site autoplay heuristics, playback deferred
  until the element is on screen - and the rejection was swallowed, so the hero
  sat on its poster frame for good. Chrome happens to retry by itself when the
  tab is shown; Safari does not.

  This hook keeps asking, politely, until the film is actually running:
    - on mount and on every readiness step (loadedmetadata / loadeddata /
      canplay / canplaythrough),
    - when the tab becomes visible again, on pageshow (bfcache) and window focus,
    - on the first user gesture (pointer / key / wheel / touch / scroll) - the
      one thing Safari always honours,
    - a watchdog every second for the first 20s (then every 3s) if the element
      is still paused,
    - and if the element claims to be playing but the clock is not moving for
      4s (a decode stall), one reload + play.
  All of it is muted, inline, and never throws - a media quirk must never
  become a runtime error.

  `enabled` = false pauses the element (the landing hero pauses a film whose
  slide is not active); flipping it back to true starts the whole dance again.
*/
export const FILM_BLOCKED = "gm:film-blocked";

/* `?filmdebug=1` - a small live read-out of the real media state (Safari on
   the client's Mac, 2026-10-17): paused / readyState / networkState / error /
   buffered / every play() rejection, so a screenshot tells us exactly why a
   film is not moving. Never shown without the flag. */
const debugOn = () => { try { return /[?&]filmdebug=1/.test(window.location.search); } catch { return false; } };

/* Real Safari (Mac + iOS), not Chrome / Firefox / Edge on Apple devices. */
export const isSafari = () => {
  try {
    const ua = navigator.userAgent || "";
    return navigator.vendor === "Apple Computer, Inc." && /Safari\//.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|Edg\/|OPR\//.test(ua);
  } catch { return false; }
};

/* "50% 40%" | "center 40%" | "left top" -> [0..1, 0..1] like object-position. */
const KEY = { left: 0, top: 0, center: 0.5, right: 1, bottom: 1 };
function parsePos(str) {
  const parts = String(str || "50% 50%").trim().split(/\s+/);
  const one = (v, fallback) => {
    if (v == null) return fallback;
    if (v in KEY) return KEY[v];
    const n = parseFloat(v);
    return Number.isFinite(n) ? Math.min(1, Math.max(0, n / 100)) : fallback;
  };
  return [one(parts[0], 0.5), one(parts[1], 0.5)];
}
const NET = ["EMPTY", "IDLE", "LOADING", "NO_SOURCE"];
const READY = ["NOTHING", "METADATA", "CURRENT", "FUTURE", "ENOUGH"];
function mountDebug(el, state) {
  const box = document.createElement("pre");
  box.setAttribute("data-testid", "film-debug");
  box.style.cssText = "position:fixed;left:8px;top:8px;z-index:100000;margin:0;padding:10px 12px;max-width:min(92vw,560px);font:11px/1.45 Menlo,monospace;color:#FFDD00;background:rgba(0,0,0,.86);border:1px solid #FFDD00;border-radius:6px;white-space:pre-wrap;pointer-events:none;transform:translateZ(0)";
  document.body.appendChild(box);
  const tick = () => {
    let buffered = "";
    try { for (let i = 0; i < el.buffered.length; i += 1) buffered += `${el.buffered.start(i).toFixed(1)}-${el.buffered.end(i).toFixed(1)}s `; } catch { /* ignore */ }
    const rect = el.getBoundingClientRect();
    box.textContent = [
      `GM film debug  ${new Date().toLocaleTimeString()}`,
      `src        ${(el.currentSrc || el.getAttribute("src") || "-").split("/").pop()}`,
      `paused ${el.paused}  ended ${el.ended}  time ${el.currentTime.toFixed(2)}  dur ${Number.isFinite(el.duration) ? el.duration.toFixed(1) : "-"}`,
      `readyState ${el.readyState} ${READY[el.readyState] || ""}   networkState ${el.networkState} ${NET[el.networkState] || ""}`,
      `error      ${el.error ? `${el.error.code} ${el.error.message || ""}` : "none"}`,
      `buffered   ${buffered || "-"}`,
      `muted ${el.muted} attr:${el.hasAttribute("muted")}  autoplay:${el.hasAttribute("autoplay")}  inline:${el.hasAttribute("playsinline")}  loop ${el.loop}`,
      `size ${Math.round(rect.width)}x${Math.round(rect.height)} @ ${Math.round(rect.left)},${Math.round(rect.top)}  visible:${document.visibilityState}  focus:${document.hasFocus()}`,
      `canPlay mp4/avc1: "${el.canPlayType('video/mp4; codecs="avc1.640029"')}"  reduced-motion:${window.matchMedia("(prefers-reduced-motion: reduce)").matches}`,
      `play() calls ${state.attempts}  ok ${state.ok}  rejected ${state.rejected}${state.lastErr ? `  last: ${state.lastErr}` : ""}`,
      `safari-fix ${state.safariFix ? "ON (layout)" : "off"}  fit ${state.fit || "-"}  object-fit ${el.style.objectFit || "cover"}  left/top ${el.style.left || "-"}/${el.style.top || "-"}`,
      `events     ${state.events.slice(-8).join(" ")}`,
      `UA ${navigator.userAgent.replace(/^Mozilla\/5\.0 /, "")}`,
    ].join("\n");
  };
  tick();
  const id = setInterval(tick, 2000);
  return () => { clearInterval(id); box.remove(); };
}

export function useAutoplayFilm(ref, enabled = true) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let dead = false;
    let attempts = 0;
    let reloads = 0;
    let lastT = -1;
    let stillSince = 0;
    let blockedAnnounced = false;
    const state = { attempts: 0, ok: 0, rejected: 0, lastErr: "", events: [] };
    const note = (s) => { state.events.push(s); if (state.events.length > 40) state.events.shift(); };
    const unmountDebug = debugOn() ? mountDebug(el, state) : null;
    const evNames = ["loadstart", "loadedmetadata", "loadeddata", "canplay", "canplaythrough", "play", "playing", "pause", "waiting", "stalled", "suspend", "abort", "error", "emptied", "ended"];
    const onAny = (e) => note(e.type);
    evNames.forEach((n) => el.addEventListener(n, onAny));
    // Safari has been seen keeping the poster painted over a running film
    // (object-fit: cover + poster). Once frames flow, drop the poster - the
    // slide behind the element carries the same image as its background.
    const dropPoster = () => { if (!isSafari() && el.currentTime > 0.05 && el.hasAttribute("poster")) { el.removeAttribute("poster"); note("poster-off"); } };
    el.addEventListener("timeupdate", dropPoster);
    el.addEventListener("playing", dropPoster);

    // Safari on Mac (client read-out, 2026-10-17): the element reports
    // paused=false, time advancing, readyState 4 - and the picture never
    // changes. That is WebKit's object-fit compositing bug (bugs.webkit.org
    // 240786 / Apple forums 707247): with `object-fit: cover` the platform
    // video layer sits behind a clipping layer that is not invalidated, so
    // the first frame stays on screen while playback runs underneath. The
    // production workaround is to NOT use object-fit on Safari: size the
    // element to cover the box ourselves (same maths as `cover`, honouring
    // object-position) and move it with a 3D transform, which also gives the
    // video its own compositor layer. Visually identical; Safari only.
    const safari = isSafari();
    state.safariFix = safari;
    let ro = null;
    const fitCover = () => {
      if (!safari || dead) return;
      const vw = el.videoWidth, vh = el.videoHeight;
      const box = el.parentElement;
      if (!vw || !vh || !box) return;
      const W = box.clientWidth, H = box.clientHeight;
      if (!W || !H) return;
      if (!el.dataset.gmPos) el.dataset.gmPos = getComputedStyle(el).objectPosition || "50% 50%";
      const [px, py] = parsePos(el.dataset.gmPos);
      const s = Math.max(W / vw, H / vh);
      const w = Math.ceil(vw * s), h = Math.ceil(vh * s);
      const x = Math.round((W - w) * px), y = Math.round((H - h) * py);
      const next = `${w}x${h} @ ${x},${y} in ${W}x${H}`;
      if (state.fit === next) return;
      // Plain layout sizing, exactly like Safari's own media document: no
      // transform, no will-change, no backface trick on the element. Frame
      // pacing audit (client, 2026-10-18): the bare file was smooth and the
      // page dropped frames with a translate3d'd, will-change'd video - so the
      // element is now the simplest thing WebKit can hand to its video layer.
      Object.assign(el.style, {
        objectFit: "fill", inset: "auto", left: `${x}px`, top: `${y}px`, right: "auto", bottom: "auto",
        width: `${w}px`, height: `${h}px`, maxWidth: "none", maxHeight: "none",
        transform: "none", willChange: "auto", backfaceVisibility: "visible",
      });
      state.fit = next;
    };
    if (safari) {
      el.addEventListener("loadedmetadata", fitCover);
      el.addEventListener("resize", fitCover);
      fitCover();
      if ("ResizeObserver" in window && el.parentElement) { ro = new ResizeObserver(fitCover); ro.observe(el.parentElement); }
      window.addEventListener("orientationchange", fitCover);
    }

    const prime = () => {
      try {
        el.muted = true;
        el.defaultMuted = true;
        el.setAttribute("muted", "");
        el.setAttribute("playsinline", "");
        el.setAttribute("webkit-playsinline", "");
      } catch { /* ignore */ }
    };

    const tryPlay = (why) => {
      if (dead || !enabled) return;
      if (!el.paused && !el.ended) return;
      prime();
      attempts += 1;
      state.attempts = attempts;
      note(`play:${why}`);
      let p;
      try { p = el.play(); } catch (err) { p = null; state.rejected += 1; state.lastErr = `${err && err.name}: ${err && err.message}`; }
      if (p && typeof p.then === "function") {
        p.then(() => { state.ok += 1; }).catch((err) => {
          state.rejected += 1;
          state.lastErr = `${err && err.name}: ${err && err.message}`;
          // Autoplay refused (NotAllowedError) or the file cannot be decoded
          // (NotSupportedError) - tell the loader not to wait for a first frame.
          if (!blockedAnnounced && err && (err.name === "NotAllowedError" || err.name === "NotSupportedError")) {
            blockedAnnounced = true;
            try { window.dispatchEvent(new CustomEvent(FILM_BLOCKED, { detail: { why } })); } catch { /* ignore */ }
          }
        });
      }
    };

    if (!enabled) {
      try { el.pause(); } catch { /* ignore */ }
      return undefined;
    }

    prime();
    // Safari: with `src` already set and `autoplay` present, an explicit load()
    // before the first play() makes the resource selection deterministic.
    try { if (el.readyState === 0 && el.networkState !== 2) el.load(); } catch { /* ignore */ }
    tryPlay("mount");

    const onReady = () => tryPlay("ready");
    ["loadedmetadata", "loadeddata", "canplay", "canplaythrough"].forEach((ev) => el.addEventListener(ev, onReady));

    const onVis = () => { if (document.visibilityState === "visible") tryPlay("visible"); };
    document.addEventListener("visibilitychange", onVis);
    const onShow = () => tryPlay("pageshow");
    window.addEventListener("pageshow", onShow);
    window.addEventListener("focus", onShow);

    // First user gesture: Safari always allows play() from inside one.
    const gestures = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"];
    const onGesture = () => {
      tryPlay("gesture");
      if (!el.paused) gestures.forEach((g) => window.removeEventListener(g, onGesture));
    };
    gestures.forEach((g) => window.addEventListener(g, onGesture, { passive: true }));

    // Watchdog: paused -> ask again; "playing" but frozen -> one reload.
    const started = Date.now();
    const tick = setInterval(() => {
      if (dead) return;
      if (el.paused && !el.ended) {
        const age = Date.now() - started;
        if (age < 20000 || Math.floor(age / 1000) % 3 === 0) tryPlay("watchdog");
        return;
      }
      const t = el.currentTime;
      if (t === lastT && el.readyState >= 2) {
        stillSince += 1;
        if (stillSince >= 4 && reloads < 2) {
          reloads += 1;
          stillSince = 0;
          try { el.load(); } catch { /* ignore */ }
          tryPlay("stall-reload");
        }
      } else {
        stillSince = 0;
      }
      lastT = t;
    }, 1000);

    return () => {
      dead = true;
      clearInterval(tick);
      evNames.forEach((n) => el.removeEventListener(n, onAny));
      el.removeEventListener("timeupdate", dropPoster);
      el.removeEventListener("playing", dropPoster);
      el.removeEventListener("loadedmetadata", fitCover);
      el.removeEventListener("resize", fitCover);
      window.removeEventListener("orientationchange", fitCover);
      if (ro) ro.disconnect();
      if (unmountDebug) unmountDebug();
      ["loadedmetadata", "loadeddata", "canplay", "canplaythrough"].forEach((ev) => el.removeEventListener(ev, onReady));
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pageshow", onShow);
      window.removeEventListener("focus", onShow);
      gestures.forEach((g) => window.removeEventListener(g, onGesture));
    };
  }, [ref, enabled]);
}
